import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import config from '../config';
import error from '../assets/Tr3.jpg';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../styles/mediaSlideshow.css'; // Import the CSS file for styling
import '../styles/mobileSlideshow.css'; // Import mobile-specific styles

const NextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
        <div
            className={`${className} custom-arrow custom-arrow-next`}
            style={{ ...style, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2 }}
            onClick={onClick}
        />
    );
};

const PrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
        <div
            className={`${className} custom-arrow custom-arrow-prev`}
            style={{ ...style, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2 }}
            onClick={onClick}
        />
    );
};

const MediaSlideshow = ({ media, transitionEffect = 'ease', fadeEffect = false, disableFullscreen = false, caption, condition, product, onShareUpdate }) => {
    // All hooks must be called at the top level, unconditionally
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [shareCount, setShareCount] = useState(0);
    const sliderRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const location = useLocation();
    // Track mobile status with state
    const [isMobile, setIsMobile] = useState(false);
    
    // Fail-safe check for valid media array
    const hasValidMedia = media && Array.isArray(media) && media.length > 0;

    // Effect to detect mobile and handle window resize
    useEffect(() => {
        // Check if window is defined (for SSR compatibility)
        if (typeof window === 'undefined') return;
        
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        // Check on mount
        checkMobile();
        
        // Listen for window resize events
        window.addEventListener('resize', checkMobile);
        
        // Cleanup
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Keep track of incoming share totals from parent product object
    useEffect(() => {
        if (product && typeof product.shares === 'number') {
            setShareCount(product.shares);
        }
    }, [product?.shares]);

    // Add effect to pre-load images and handle loading states
    useEffect(() => {
        // Reset loading state when media changes
        setIsLoading(true);
        
        if (hasValidMedia && media[0].type === 'image') {
            const img = new Image();
            img.onload = () => setIsLoading(false);
            img.onerror = () => setIsLoading(false);
            img.src = media[0].url;
        } else {
            // If no media or first item is video, don't show loading
            setIsLoading(false);
        }
    }, [media, hasValidMedia]);

    // Preload adjacent images for smooth navigation
    useEffect(() => {
        if (!hasValidMedia) return;
        
        // Preload next and previous images (up to 2 in each direction)
        const imagesToPreload = [];
        for (let i = -2; i <= 2; i++) {
            if (i === 0) continue; // Skip current image
            const index = (currentIndex + i + media.length) % media.length;
            if (media[index] && media[index].type === 'image') {
                imagesToPreload.push(media[index].url);
            }
        }
        
        // Create Image objects to trigger browser preloading
        imagesToPreload.forEach(url => {
            const img = new Image();
            img.src = url;
        });
    }, [currentIndex, media, hasValidMedia]);
    
    const handleImageLoad = () => {
        // Clear loading state immediately to ensure image visibility
        setIsLoading(false);
    };

    const handleSingleClick = (index, event) => {
        if (!hasValidMedia) return;
        
        event.preventDefault(); // Prevent default behavior
        event.stopPropagation(); // Stop propagation of the event
        if (sliderRef.current) {
            sliderRef.current.slickNext();
        }
    };

    const handleShareClick = async (e) => {
        e.stopPropagation();
        if (!hasValidMedia || !product) return;
        
        const currentUrl = window.location.origin + location.pathname;
        try {
            const headers = {
                'Content-Type': 'application/json'
            };
            const token = Cookies.get('authToken');
            if (token) {
                headers['Authorization'] = `Token ${token}`;
            }
            const shareResponse = await fetch(`${config.backendUrl}/products/shared/${product.uuid || product.id}/`, {
                method: 'POST',
                headers: headers
            });
            if (shareResponse.ok) {
                const data = await shareResponse.json();
                let nextShares = null;

                if (typeof data?.shares === 'number') {
                    nextShares = data.shares;
                    setShareCount(data.shares);
                } else {
                    setShareCount(prev => {
                        const updated = prev + 1;
                        nextShares = updated;
                        return updated;
                    });
                }

                if (typeof onShareUpdate === 'function' && typeof nextShares === 'number') {
                    onShareUpdate(nextShares);
                }
                if (navigator.share) {
                    await navigator.share({
                        title: 'Partager ce produit',
                        text: product.name,
                        url: currentUrl
                    });
                } else {
                    navigator.clipboard.writeText(currentUrl).then(() => {
                        setShowCopiedMessage(true);
                        setTimeout(() => setShowCopiedMessage(false), 2000);
                    });
                }
            }
        } catch (error) {
            console.error('Error sharing:', error);
            // Attempt to copy to clipboard as fallback
            try {
                navigator.clipboard.writeText(currentUrl).then(() => {
                    setShowCopiedMessage(true);
                    setTimeout(() => setShowCopiedMessage(false), 2000);
                });
            } catch (clipboardError) {
                console.error('Could not copy to clipboard:', clipboardError);
            }
        }
    };

    const handleLikeClick = async (e) => {
        e.stopPropagation();
        if (!product) return;
        
        // If onLikeToggle function is provided, use it
        if (typeof product.onLikeToggle === 'function') {
            product.onLikeToggle();
            return;
        }
        
        // Otherwise, fall back to the original implementation
        try {
            const headers = {
                'Content-Type': 'application/json'
            };
            const token = Cookies.get('authToken');
            if (token) {
                headers['Authorization'] = `Token ${token}`;
            } else {
                console.error('No auth token found');
                return;
            }

            // Optimistically update UI
            const newLikeState = !product.isLikedByUser;
            // Update the product in parent component
            if (typeof product.onLikeToggle === 'function') {
                product.onLikeToggle(newLikeState);
            }

            const likeResponse = await fetch(`${config.backendUrl}/products/${newLikeState ? 'like' : 'unlike'}/${product.uuid || product.id}/`, {
                method: 'POST',
                headers: headers
            });
            
            if (!likeResponse.ok) {
                // Revert optimistic update on error
                if (typeof product.onLikeToggle === 'function') {
                    product.onLikeToggle(!newLikeState);
                }
                console.error('Like operation failed:', await likeResponse.text());
            }
        } catch (error) {
            console.error('Error toggling like:', error);
        }
    };

    const handleDoubleClick = () => {
        if (!disableFullscreen) {
            setIsFullScreen(true);
        }
    };
    
    const handleImageError = (e) => {
        e.target.src = error;
        setIsLoading(false); // Ensure loading indicator is hidden even on error
    };

    const handleCloseClick = () => {
        setIsFullScreen(false);
    };

    const handleNext = () => {
        if (!hasValidMedia) return;
        setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length);
    };

    const handlePrev = () => {
        if (!hasValidMedia) return;
        setCurrentIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length);
    };

    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        cssEase: transitionEffect,
        fade: fadeEffect,
        beforeChange: (current, next) => setCurrentIndex(next),
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    infinite: true,
                    dots: true,
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    initialSlide: 1,
                    dots: true,
                }
            },
        ]
    };

    // If no valid media, return fallback display
    if (!hasValidMedia) {
        return (
            <div className="mobile-slideshow-container" style={{display:'flex',justifyContent:'center',alignItems:'center'}}>
                <img src={error} alt="No media available" style={{maxWidth:'100%',maxHeight:'100%'}} />
            </div>
        );
    }

    // Always use the simpler, more reliable approach regardless of screen size
    // This solves the issue with white screens on desktop
    return (
        <div className="mobile-slideshow-container">
            <div className="mobile-image-container">
                <img
                    src={media[currentIndex]?.url || error}
                    alt={`Product image ${currentIndex + 1}`}
                    onError={(e) => e.target.src = error}
                    className="mobile-slideshow-image"
                />
            </div>
            
            {/* Simple navigation controls */}
            <div 
                className="mobile-nav-button mobile-prev-button"
                onClick={() => setCurrentIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length)}
            >
                ‹
            </div>
            
            <div 
                className="mobile-nav-button mobile-next-button"
                onClick={() => setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length)}
            >
                ›
            </div>
            
            {/* Image counter */}
            <div className="mobile-image-counter">
                {currentIndex + 1} / {media.length}
            </div>
            
            {/* Add price display */}
            {product && product.price && product.currency && (
                <div className="mobile-product-price">
                    {product.currency} {product.price}
                </div>
            )}
            
            {/* Product controls like in original */}
            <div className="product-icon-container">
                <div className="likes-views">
                    <span
                        className={`product-likes ${product?.isLikedByUser ? 'liked' : ''}`}
                        onClick={handleLikeClick}
                    >
                        <i className={`fas fa-heart ${product?.isLikedByUser ? 'viewed' : ''}`}></i>
                        <span className="likes-count">{product?.likes ?? 0}</span>
                    </span>
                    <span className="product-views">
                        <i className="fas fa-eye"></i>
                        <span className="share-count">{product?.views ?? 0}</span>
                    </span>
                    <span className="product-share" onClick={handleShareClick}>
                        <i className="fas fa-share"></i>
                        <span className="share-count">{shareCount}</span>
                        {showCopiedMessage && (
                            <span className="copied-message">
                                URL copiée!
                            </span>
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
    
    // Add fullscreen overlay
    return (
        <div className="media-slideshow-container" onDoubleClick={handleDoubleClick}>
            <Slider ref={sliderRef} {...settings}>
                {media.map((item, index) => (
                    <div
                        key={index}
                        className="media-slide"
                        onClick={(event) => handleSingleClick(index, event)}
                    >
                        {item.type === 'image' ? (
                            <>
                                {isLoading && <div className="media-loading">Loading...</div>}
                                <img
                                    src={item.url}
                                    alt={`Slide ${index}`}
                                    className="media-image"
                                    onLoad={handleImageLoad}
                                    onError={handleImageError}
                                />
                            </>
                        ) : (
                            <video
                                controls
                                className="media-video"
                                onLoadedData={handleImageLoad}
                                onError={handleImageError}
                            >
                                <source src={item.url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        )}
                        {caption && (
                            <div className="media-caption">
                                <span>{caption}</span>
                            </div>
                        )}
                        {condition && (
                            <div className={`media-condition ${condition === 'Usagé' ? 'used' : 'new'}`}>
                                <span>{condition}</span>
                            </div>
                        )}
                        {product && product.currency && product.price && (
                            <div className="media-product-price">
                                {product.currency} {product.price}
                            </div>
                        )}
                    </div>
                ))}
            </Slider>
            <div className="product-icon-container">
                <div className="likes-views">
                    <span
                        className={`product-likes ${product?.isLikedByUser ? 'liked' : ''}`}
                        onClick={handleLikeClick}
                    >
                        <i className={`fas fa-heart ${product?.isLikedByUser ? 'viewed' : ''}`}></i>
                        <span className="likes-count">{product?.likes ?? 0}</span>
                    </span>
                    <span className="product-views">
                        <i className="fas fa-eye"></i>
                        <span className="share-count">{product?.views ?? 0}</span>
                    </span>
                    <span className="product-share" onClick={handleShareClick}>
                        <i className="fas fa-share"></i>
                        <span className="share-count">{shareCount}</span>
                        {showCopiedMessage && (
                            <span className="copied-message">
                                URL copiée!
                            </span>
                        )}
                    </span>
                </div>
            </div>
            {isFullScreen && (
                <div className="fullscreen-overlay">
                    <button className="close-button" onClick={handleCloseClick}>×</button>
                    <div className="fullscreen-content">
                        <button className="fullscreen-arrow fullscreen-arrow-prev" onClick={handlePrev}>‹</button>
                        {media[currentIndex].type === 'image' ? (
                            <img
                                src={media[currentIndex].url}
                                alt={`Slide ${currentIndex}`}
                                className="fullscreen-image"
                                onError={handleImageError}
                            />
                        ) : (
                            <video controls className="fullscreen-video">
                                <source src={media[currentIndex].url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        )}
                        <button className="fullscreen-arrow fullscreen-arrow-next" onClick={handleNext}>›</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaSlideshow;